import React, { useState, useEffect } from 'react';
import { useComparison } from '@/hooks/useComparison';
import { Button } from './ui/button';
import { Scale, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/config/axios';
import { Property } from '@/types/property';
import { GridFSImage } from './GridFSImage';

export function ComparisonBar() {
  const { compareIds, removeFromCompare, clearCompare } = useComparison();
  const [properties, setProperties] = useState<Property[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProperties = async () => {
      if (compareIds.length === 0) {
        setProperties([]);
        return;
      }
      try {
        // En un caso real podrías tener un endpoint para múltiples IDs
        const promises = compareIds.map(id => axiosInstance.get(`/api/properties/${id}`));
        const responses = await Promise.all(promises);
        setProperties(responses.map(r => r.data.property));
      } catch (error) {
        console.error('Error fetching properties for bar:', error);
      }
    };
    fetchProperties();
  }, [compareIds]);

  if (compareIds.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-ink border-t-4 border-terracotta p-4 animate-in slide-in-from-bottom duration-500">
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 text-parchment">
            <div className="bg-terracotta p-2">
              <Scale className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-widest">Comparar Propiedades</p>
              <p className="text-[10px] font-serif italic text-parchment/60">{compareIds.length} de 3 seleccionadas</p>
            </div>
          </div>

          <div className="flex gap-4">
            {properties.map((prop) => (
              <div key={prop._id || prop.id} className="relative group">
                <div className="relative h-16 w-16 brutalist-border-sm overflow-hidden">
                  <GridFSImage 
                    src={prop.images?.[0] || ''} 
                    alt={prop.title} 
                    size="sm" 
                    className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all"
                  />
                </div>
                <button 
                  onClick={() => {
                    const id = prop._id || prop.id;
                    if (id) removeFromCompare(id.toString());
                  }}
                  className="absolute -top-2 -right-2 bg-terracotta text-parchment p-1 rounded-none hover:bg-ink border border-parchment/20"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {Array.from({ length: 3 - compareIds.length }).map((_, i) => (
              <div key={`empty-${i}`} className="h-16 w-16 border-2 border-dashed border-parchment/20 flex items-center justify-center">
                <span className="text-parchment/20 text-xs">?</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button 
            onClick={clearCompare}
            className="text-[10px] font-black uppercase tracking-widest text-parchment/40 hover:text-terracotta transition-colors"
          >
            Limpiar Todo
          </button>
          <Button 
            variant="terracotta" 
            size="lg"
            className="h-14 px-10 group"
            onClick={() => navigate('/comparar')}
            disabled={compareIds.length < 2}
          >
            COMPARAR AHORA <ArrowRight className="ml-4 h-4 w-4 group-hover:translate-x-2 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  );
}
